
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePrivacy } from '../context/PrivacyContext';
import { useTheme } from '../context/ThemeContext';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { LayoutDashboard, Users, Target, Glasses, Menu, X, FileBarChart, Package, DollarSign, LogOut, Briefcase, Eye, EyeOff, Sun, Moon, ChevronLeft, ChevronRight, Cake, Settings, Lock } from 'lucide-react';
import { BirthdayNotification } from './BirthdayNotification';
import { ExpiringPrescriptionsNotification } from './ExpiringPrescriptionsNotification';
import { StoreSettingsModal } from './settings/StoreSettingsModal';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, tenant, logout } = useAuth();
  const { valuesVisible, toggleValuesVisibility, requireUnlock } = usePrivacy();
  const { isDark, toggleTheme } = useTheme();
  const { storeSettings, openSettingsModal } = useStoreSettings();
  const navigate = useNavigate();

  const handleOpenSettings = () => {
    if (!requireUnlock('acessar e alterar as Configurações da Loja')) {
      return;
    }
    openSettingsModal();
  };
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('optisaas_sidebar_collapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const location = useLocation();

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('optisaas_sidebar_collapsed', String(next));
      return next;
    });
  };

  React.useEffect(() => {
    // Dynamic SEO Management
    document.title = "OptiSaaS - Sistema para Óticas";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'OptiSaaS é a plataforma completa para gestão inteligente de óticas.');

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', 'OptiSaaS - Sistema para Óticas');
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Painel Gerencial', icon: LayoutDashboard },
    { path: '/crm', label: 'CRM & Fidelização', icon: Target },
    { path: '/customers', label: 'Clientes', icon: Users },
    { path: '/orders', label: 'Ordens de Serviço', icon: Glasses },
    { path: '/inventory', label: 'Estoque', icon: Package },
    { path: '/finance', label: 'Financeiro', icon: DollarSign },
    { path: '/reports', label: 'Relatórios', icon: FileBarChart },
    { path: '/birthdays', label: 'Aniversariantes', icon: Cake },
  ];

  // Add Admin Panel for SUPERADMIN
  if (user?.role === 'SUPERADMIN') {
    navItems.push({ path: '/admin', label: 'Painel ADM', icon: Briefcase });
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-dvh min-h-0 overflow-hidden bg-slate-50 dark:bg-slate-900">
      {/* Mobile Sidebar Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-30 flex h-dvh min-h-0 shrink-0 flex-col bg-[#070b16] text-white border-r border-slate-800/70
          transform transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'lg:w-20' : 'lg:w-72'}
          w-72
        `}
      >
        {/* Brand Header */}
        <div className={`flex h-20 shrink-0 items-center border-b border-slate-800/80 bg-[#050811] transition-all duration-300 justify-between px-6 ${isCollapsed ? 'lg:justify-center lg:px-3' : 'lg:justify-between lg:px-6'}`}>
          {/* Logo icon — always visible */}
          <div className={`flex items-center group cursor-pointer transition-all duration-300 space-x-3 ${isCollapsed ? 'lg:justify-center lg:space-x-0' : 'lg:space-x-3'}`}>
            <div className="relative shrink-0 p-2.5 bg-blue-600 rounded-xl shadow-md shadow-blue-600/30 group-hover:scale-105 transition-transform duration-300">
              <Glasses className="w-6 h-6 text-white" />
            </div>
            {/* Labels — hidden on desktop only when collapsed */}
            <div className={`overflow-hidden transition-all duration-300 w-auto opacity-100 ${isCollapsed ? 'lg:w-0 lg:opacity-0 lg:pointer-events-none' : 'lg:w-auto lg:opacity-100'}`}>
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-lg font-black tracking-tight text-white uppercase">Opti<span className="text-blue-500 font-extrabold">.</span>SaaS</span>
                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">PRO</span>
              </div>
              <p className="text-[10px] text-slate-200 font-bold uppercase tracking-wider whitespace-nowrap">Gestão Óptica</p>
            </div>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-slate-300 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="my-4 min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 custom-scrollbar">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                title={isCollapsed ? item.label : undefined}
                className={`
                  relative flex items-center py-3 rounded-xl transition-all duration-200 group overflow-hidden text-sm px-4
                  ${isCollapsed ? 'lg:justify-center lg:px-0' : 'lg:px-4'}
                  ${active
                    ? 'bg-blue-600 text-white font-bold shadow-md shadow-blue-600/30 ring-1 ring-white/15'
                    : 'text-slate-200 hover:text-white hover:bg-slate-800/80 font-medium'}
                `}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                {active && (
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-white rounded-r-full shadow-sm ${isCollapsed ? 'lg:hidden' : ''}`} />
                )}
                <Icon className={`w-5 h-5 shrink-0 transition-colors duration-200 mr-3 ${isCollapsed ? 'lg:mr-0' : 'lg:mr-3'} ${active ? 'text-white' : 'text-slate-300 group-hover:text-blue-400'}`} />
                <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 w-auto opacity-100 ${isCollapsed ? 'lg:w-0 lg:opacity-0' : 'lg:w-auto lg:opacity-100'} ${active ? 'translate-x-1' : 'group-hover:translate-x-1'} transition-transform duration-200 tracking-wide`}>
                  {item.label}
                </span>
                {active && <div className="absolute inset-0 bg-white/5 pointer-events-none" />}
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle Button — Desktop only */}
        <div className={`hidden lg:flex shrink-0 border-t border-slate-800/60 p-3 ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
          <button
            onClick={handleToggleCollapse}
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800/80 transition-all duration-200 text-xs font-bold"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>Recolher</span>
              </>
            )}
          </button>
        </div>

        {/* Tenant & User Bottom Card */}
        <div className={`shrink-0 bg-[#050811] border-t border-slate-800/80 transition-all duration-300 p-4 sm:p-5 ${isCollapsed ? 'lg:p-3' : 'lg:p-4 sm:lg:p-5'}`}>
          <div className={`rounded-2xl bg-[#0c1427] border border-slate-800 shadow-inner transition-all duration-300 p-3.5 ${isCollapsed ? 'lg:p-2' : 'lg:p-3.5'}`}>
            <div className={`flex items-center mb-3 transition-all duration-300 space-x-3 ${isCollapsed ? 'lg:justify-center lg:space-x-0' : 'lg:space-x-3'}`}>
              <div className="w-10 h-10 rounded-xl bg-blue-600 p-[1.5px] shadow-md shadow-blue-500/20 shrink-0 overflow-hidden flex items-center justify-center">
                {storeSettings.logoUrl ? (
                  <img src={storeSettings.logoUrl} alt="Logo" className="w-full h-full object-cover rounded-[9px]" />
                ) : (
                  <div className="w-full h-full rounded-[9px] bg-[#0c1427] flex items-center justify-center text-xs font-black text-white uppercase">
                    {(storeSettings.attendantName || user?.name || 'D').charAt(0)}
                  </div>
                )}
              </div>
              <div className={`overflow-hidden min-w-0 flex-1 transition-all duration-300 w-auto opacity-100 ${isCollapsed ? 'lg:w-0 lg:opacity-0 lg:hidden' : 'lg:w-auto lg:opacity-100'}`}>
                <p className="text-xs font-extrabold text-white truncate leading-tight flex items-center justify-between">
                  <span className="truncate">{storeSettings.attendantName || user?.name || 'Diana'}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 shrink-0">Resp.</span>
                </p>
                <p className="text-[11px] text-slate-300 truncate mt-0.5 flex items-center gap-1.5 font-bold tracking-wide">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  <span className="truncate">{storeSettings.storeName || tenant?.name || 'Óticas Diana'}</span>
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <button
                type="button"
                onClick={handleOpenSettings}
                title={isCollapsed ? 'Configurações da Ótica' : undefined}
                className={`w-full flex items-center justify-center py-2 rounded-xl bg-blue-950/60 hover:bg-blue-600/30 text-blue-300 hover:text-white border border-blue-900/60 hover:border-blue-500/50 transition-all duration-200 text-xs font-bold tracking-wider px-3 space-x-2 ${isCollapsed ? 'lg:px-0 lg:space-x-0' : 'lg:px-3 lg:space-x-2'}`}
              >
                <Settings className="w-3.5 h-3.5 shrink-0 text-blue-400" />
                <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 w-auto opacity-100 ${isCollapsed ? 'lg:w-0 lg:opacity-0' : 'lg:w-auto lg:opacity-100'}`}>
                  Configurações da Loja
                </span>
              </button>

              <button
                onClick={handleLogout}
                title={isCollapsed ? 'Sair do Sistema' : undefined}
                className={`w-full flex items-center justify-center py-2 rounded-xl bg-slate-900 hover:bg-rose-500/20 text-slate-200 hover:text-rose-300 border border-slate-800 hover:border-rose-500/40 transition-all duration-200 text-xs font-bold tracking-wider px-3 space-x-2 ${isCollapsed ? 'lg:px-0 lg:space-x-0' : 'lg:px-3 lg:space-x-2'}`}
              >
                <LogOut className="w-3.5 h-3.5 shrink-0" />
                <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 w-auto opacity-100 ${isCollapsed ? 'lg:w-0 lg:opacity-0' : 'lg:w-auto lg:opacity-100'}`}>Sair do Sistema</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3.5 shadow-xs dark:border-slate-700 dark:bg-slate-800 lg:hidden">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(true)} className="text-slate-700 dark:text-slate-300 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
              <Menu className="w-6 h-6" />
            </button>
            <span className="font-extrabold text-slate-900 text-sm">{tenant?.name || 'OptiSaaS'}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Theme Toggle (Mobile) */}
            <button
              id="theme-toggle-mobile"
              onClick={toggleTheme}
              className="p-2 rounded-xl text-slate-500 hover:text-slate-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title={isDark ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
              aria-label={isDark ? 'Ativar Modo Claro' : 'Ativar Modo Escuro'}
            >
              {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-blue-600" />}
            </button>

            <button
              id="privacy-toggle-mobile"
              onClick={toggleValuesVisibility}
              className={`p-2 rounded-xl transition-all duration-200 border ${
                valuesVisible
                  ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border-transparent dark:bg-[#1E293B] dark:border-[#334155] dark:text-[#E2E8F0]'
                  : 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300 dark:bg-amber-950/40 dark:border-amber-700/60 dark:text-amber-300 shadow-xs'
              }`}
              title={valuesVisible ? 'Ocultar valores e travar edições' : 'Valores Ocultos (Clique para digitar o PIN)'}
              aria-label={valuesVisible ? 'Ocultar valores monetários' : 'Digitar PIN para mostrar valores'}
            >
              {valuesVisible ? (
                <Eye className="w-5 h-5 text-slate-400 dark:text-[#E2E8F0]" />
              ) : (
                <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-pulse" />
              )}
            </button>
            <button
              onClick={handleOpenSettings}
              className="p-2 rounded-xl text-slate-500 hover:text-blue-600 dark:text-slate-300 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Configurações da Ótica"
            >
              <Settings className="w-5 h-5" />
            </button>
            <BirthdayNotification />
            <ExpiringPrescriptionsNotification />
          </div>
        </header>

        {/* Desktop Top Bar */}
        <header className="sticky top-0 z-20 hidden items-center justify-between border-b border-slate-200 bg-white px-6 py-2.5 shadow-[0_1px_3px_0_rgba(15,23,42,0.03)] dark:border-slate-700 dark:bg-slate-800 lg:flex">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">Sistema Óptico</span>
            <span className="text-slate-300 dark:text-slate-600 font-bold">/</span>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              {navItems.find(item => isActive(item.path))?.label || 'OptiSaaS'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Store indicator (clicável para abrir configurações) */}
            <button
              type="button"
              onClick={handleOpenSettings}
              title="Clique para editar dados da Ótica & Atendente"
              className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100/80 dark:bg-slate-800/80 hover:bg-blue-50 dark:hover:bg-slate-700/80 border border-slate-200/70 dark:border-slate-700/80 hover:border-blue-300 dark:hover:border-blue-600 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all group"
            >
              {storeSettings.logoUrl ? (
                <img src={storeSettings.logoUrl} alt="Logo" className="w-4 h-4 rounded object-cover" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              )}
              <span className="truncate max-w-[150px]">{storeSettings.storeName || tenant?.name || 'Óticas Diana'}</span>
              <Settings className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 transition-colors ml-0.5" />
            </button>

            {/* Botão Configurações da Loja no Desktop */}
            <button
              onClick={handleOpenSettings}
              className="group flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-700/80 dark:hover:text-blue-400 transition-all text-xs font-bold shadow-xs active:scale-95"
              title="Configurações da Ótica & Atendente"
            >
              <Settings className="w-3.5 h-3.5 text-blue-500 transition-transform duration-300 group-hover:rotate-90" />
              <span className="text-[11px] font-black uppercase tracking-wider">
                Configurar Loja
              </span>
            </button>

            {/* Theme Toggle (Desktop: Modo Claro / Modo Escuro) */}
            <button
              id="theme-toggle-desktop"
              onClick={toggleTheme}
              className="group flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-all text-xs font-bold shadow-xs active:scale-95"
              title={isDark ? 'Mudar para Modo Claro (Branco)' : 'Mudar para Modo Escuro (Dark)'}
              aria-label={isDark ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
            >
              {isDark ? (
                <>
                  <Sun className="w-4 h-4 text-amber-400 transition-transform duration-300 group-hover:rotate-45" />
                  <span className="text-[11px] font-black uppercase tracking-wider text-amber-300">
                    Modo Claro
                  </span>
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 text-blue-600 transition-transform duration-300 group-hover:-rotate-12" />
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 dark:text-slate-200">
                    Modo Escuro
                  </span>
                </>
              )}
            </button>

            {/* Privacy toggle com Trava por PIN */}
            <button
              id="privacy-toggle-desktop"
              onClick={toggleValuesVisibility}
              className={`group flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all duration-200 text-xs font-bold ${
                valuesVisible
                  ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 border border-slate-200/70 dark:bg-[#1E293B] dark:border-[#334155] dark:text-[#E2E8F0] dark:hover:bg-[#26334D]'
                  : 'text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 dark:bg-amber-950/40 dark:border-amber-700/60 dark:text-amber-300 shadow-xs'
              }`}
              title={valuesVisible ? 'Ocultar valores monetários e travar edições' : 'Valores Ocultos (Clique para digitar o PIN)'}
              aria-label={valuesVisible ? 'Ocultar valores monetários' : 'Digitar PIN para mostrar valores'}
            >
              {valuesVisible ? (
                <>
                  <Eye className="w-4 h-4 text-slate-400 group-hover:text-slate-700 dark:text-[#E2E8F0]" />
                  <span className="uppercase tracking-wider text-[11px]">
                    Ocultar Valores
                  </span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
                  <span className="uppercase tracking-wider text-[11px] font-black">
                    Valores Ocultos (PIN 🔒)
                  </span>
                </>
              )}
            </button>

            <div className="h-5 w-px bg-slate-200/80 dark:bg-slate-700" />
            <BirthdayNotification />
            <ExpiringPrescriptionsNotification />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-slate-50 p-3 dark:bg-slate-900 lg:px-6 lg:py-3.5 custom-scrollbar">
          <div className="mx-auto min-h-full min-w-0 max-w-[1600px]">
            {children}
          </div>
        </main>
      </div>

      {/* Modal de Configurações da Loja e Atendente */}
      <StoreSettingsModal />
    </div>
  );
};

export default Layout;
// <title> SEO handled dynamically
